import { useState } from "react";
import { Case } from "@/types/workflow";
import { useCaseData } from "@/hooks/useCaseData";
import { useToast } from "@/hooks/use-toast";
import { WebCasesContent } from "./platforms/WebCasesContent";
import { MobileCasesContent } from "./platforms/MobileCasesContent";
import { useMediaQuery } from "@/hooks/use-media-query";

export function CasesContent() {
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const { toast } = useToast();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const {
    cases,
    isLoading,
    error,
    refetch: handleDataRefresh,
    updateCase,
    deleteCase,
  } = useCaseData();

  const filteredCases = cases.filter(caseData => {
    const matchesSearch = 
      caseData.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      caseData.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilters = activeFilters.length === 0 || 
      activeFilters.includes(caseData.status);

    return matchesSearch && matchesFilters;
  });

  const handleCreateCase = () => {
    setCreateDialogOpen(true);
    toast({
      title: "Opprett ny sak",
      description: "La oss hjelpe deg med å opprette en ny sak",
      className: "bg-amber-50 border-amber-200",
    });
  };

  const handleRefresh = async () => {
    await handleDataRefresh();
  };

  const handleEditSubmit = async (values: any) => {
    await updateCase.mutateAsync(values);
    await handleRefresh();
  };

  const handleDeleteConfirm = async () => {
    if (selectedCase) {
      await deleteCase.mutateAsync(selectedCase.id);
      await handleRefresh();
    }
  };

  const commonProps = {
    selectedCase,
    createDialogOpen,
    editDialogOpen,
    deleteDialogOpen,
    searchTerm,
    activeFilters,
    filteredCases,
    isLoading,
    error: error as Error,
    onCreateCase: handleCreateCase,
    onEditDialogChange: setEditDialogOpen,
    onCreateDialogChange: setCreateDialogOpen,
    onDeleteDialogChange: setDeleteDialogOpen,
    onSearchChange: setSearchTerm,
    onFilterChange: setActiveFilters,
    onDataRefresh: handleRefresh,
    onEditSubmit: handleEditSubmit,
    onCreateSuccess: handleRefresh,
    onDeleteConfirm: handleDeleteConfirm,
    setSelectedCase,
  };

  return isMobile 
    ? <MobileCasesContent {...commonProps} />
    : <WebCasesContent {...commonProps} />;
}